/* Schlagwörter (Keywords) der Player Card: feste Liste — reine Funktionen, kein DOM.
 *
 * Entscheidung Bastian, 2026-10-14: Schlagwörter werden in Player Info (Edit details) aus dieser festen Liste gewählt,
 * höchstens drei. Gespeichert werden SCHLÜSSEL (englisch, klein, z. B. "finisher") im Feld `traits` des Spielers.
 * Die Oberfläche zeigt übersetzte Namen (Sprachdateien keywords.<key>), die Player Card immer Englisch in Versalien
 * (KEYWORD_LABELS_EN).
 *
 * Altwerte (freier Text aus dem ersten Entwurf, z. B. "Strong", "Finisher"): Groß-/Kleinschreibung wird ignoriert; passt
 * ein Wert zu einem Eintrag der Liste, wird er auf den Schlüssel abgebildet. Passt er nicht, bleibt er unverändert
 * erhalten (wird als normaler Chip angezeigt, zählt zu den drei und verschwindet erst, wenn ein Berechtigter ihn
 * entfernt). Es gehen keine Daten verloren.
 *
 * Neue Schlagwörter: hier eintragen (Gruppe, Schlüssel, englischer Name) und in den vier Sprachdateien
 * keywords.<key> ergänzen — der Test prüft, dass keine Beschriftung fehlt.
 */

import { cleanTraits, TRAITS_MAX } from './playerCard.js';

/** Gruppen in der Reihenfolge der Auswahl; `key` ist der Schlüssel der Überschrift (keywordGroups.<key>). */
export const KEYWORD_GROUPS = [
  { key: 'physical', keywords: ['fast', 'agile', 'strong', 'athletic', 'aerial', 'tireless'] },
  { key: 'technical', keywords: ['finisher', 'dribbler', 'technical', 'first_touch', 'passer', 'crosser', 'long_shots', 'set_pieces'] },
  {
    key: 'mental',
    keywords: ['intelligent', 'creative', 'playmaker', 'composed', 'leader', 'versatile', 'efficient', 'flexible', 'worker', 'solid', 'ball_winner', 'positioning'],
  },
  { key: 'goalkeeper', keywords: ['shot_stopper', 'commanding', 'sweeper_keeper', 'distribution'] },
];

export const KEYWORD_KEYS = KEYWORD_GROUPS.flatMap((g) => g.keywords);

/** Englische Anzeigenamen (Player Card). */
export const KEYWORD_LABELS_EN = {
  fast: 'Fast', agile: 'Agile', strong: 'Strong', athletic: 'Athletic', aerial: 'Aerial', tireless: 'Tireless',
  finisher: 'Finisher', dribbler: 'Dribbler', technical: 'Technical', first_touch: 'First touch', passer: 'Passer',
  crosser: 'Crosser', long_shots: 'Long shots', set_pieces: 'Set pieces',
  intelligent: 'Intelligent', creative: 'Creative', playmaker: 'Playmaker', composed: 'Composed', leader: 'Leader',
  versatile: 'Versatile', efficient: 'Efficient', flexible: 'Flexible', worker: 'Worker', solid: 'Solid',
  ball_winner: 'Ball winner', positioning: 'Positioning',
  shot_stopper: 'Shot stopper', commanding: 'Commanding', sweeper_keeper: 'Sweeper keeper', distribution: 'Distribution',
};

export const isKeywordKey = (value) => KEYWORD_KEYS.includes(value);

/* Vergleichsform: klein, Leerraum/Bindestrich/Unterstrich gleich */
const squash = (s) => String(s ?? '').toLowerCase().replace(/[\s_-]+/g, '');
const BY_SQUASHED = new Map();
for (const key of KEYWORD_KEYS) {
  BY_SQUASHED.set(squash(key), key);
  BY_SQUASHED.set(squash(KEYWORD_LABELS_EN[key]), key);
}

/** Wert (Schlüssel oder Altwert) -> Schlüssel der Liste; kein Treffer: null. */
export function keywordKeyOf(value) {
  return BY_SQUASHED.get(squash(value)) || null;
}

/**
 * Schlagwörter eines Spielers normalisieren: bereinigt (getrimmt, höchstens 3, keine Doppelten), bekannte Altwerte auf
 * den Schlüssel abgebildet, Unbekanntes unverändert. Doppelte nach der Abbildung fallen weg.
 */
export function normalizeTraits(list) {
  const out = [];
  const seen = new Set();
  for (const raw of cleanTraits(list)) {
    const value = keywordKeyOf(raw) || raw;
    const id = squash(value);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(value);
    if (out.length >= TRAITS_MAX) break;
  }
  return out;
}

/** Englischer Anzeigename (Player Card); ein Altwert steht, wie er ist. */
export function keywordLabelEn(value) {
  const key = keywordKeyOf(value);
  return key ? KEYWORD_LABELS_EN[key] : String(value ?? '');
}

/** Name für die Oberfläche: übersetzt, wenn Schlüssel der Liste, sonst der Altwert unverändert. `t` = i18n-Funktion. */
export function keywordLabel(value, t) {
  const key = keywordKeyOf(value);
  return key ? t(`keywords.${key}`) : String(value ?? '');
}

/**
 * Ein Schlagwort umschalten (wählen / entfernen) unter der Grenze von drei. Gibt die neue Liste zurück; ist die
 * Grenze erreicht, bleibt die Liste beim Hinzufügen unverändert.
 */
export function toggleTrait(list, value) {
  const current = normalizeTraits(list);
  const id = squash(keywordKeyOf(value) || value);
  const has = current.some((v) => squash(v) === id);
  if (has) return current.filter((v) => squash(v) !== id);
  if (current.length >= TRAITS_MAX) return current;
  return [...current, keywordKeyOf(value) || String(value)];
}
